import type { PaymentFilters, AssessmentFilters } from '../types/payments';
import type { ExpenseFilters } from '../types/expense';
import type { DocumentFilters } from '../types/documents';

export const PAYMENT_KEYS = {
  all: ['payments'] as const,
  myPayments: (filters?: PaymentFilters) => [...PAYMENT_KEYS.all, 'mine', filters] as const,
  allPayments: (filters?: PaymentFilters) => [...PAYMENT_KEYS.all, 'admin', filters] as const,
  balance: ['payments', 'balance'] as const,
  methods: ['payment-methods'] as const,
};

export const DUES_KEYS = {
  all: ['dues'] as const,
  settings: ['dues', 'settings'] as const,
  myStatus: ['dues', 'my-status'] as const,
  summary: (year?: number) => ['dues', 'summary', year] as const,
  assessments: (duesSettingId: string, filters?: AssessmentFilters) =>
    ['dues', 'assessments', duesSettingId, filters] as const,
  branchProgress: ['dues', 'branch-progress'] as const,
};

export const SUBSCRIPTION_KEYS = {
  mine: ['subscription', 'mine'] as const,
};

export const EXPENSE_KEYS = {
  all: ['expenses'] as const,
  list: (filters?: ExpenseFilters) => [...EXPENSE_KEYS.all, 'list', filters] as const,
  detail: (id: string) => [...EXPENSE_KEYS.all, 'detail', id] as const,
};

export const IMAGE_KEYS = {
  url: (key: string) => ['image-url', key] as const,
};

export const TICKETING_KEYS = {
  all: ['ticketing'] as const,
  publicItems: ['ticketing', 'public-items'] as const,
  publicItem: (id: string) => ['ticketing', 'public-items', id] as const,
  orderConfirmation: (id: string) => ['ticketing', 'order', id] as const,
  ticketsByToken: (token: string) => ['ticketing', 'tickets-by-token', token] as const,
  myOrders: ['ticketing', 'my-orders'] as const,
  myTickets: ['ticketing', 'my-tickets'] as const,
  adminItems: (filters?: Record<string, string>) => ['ticketing', 'admin-items', filters] as const,
  adminOrders: (filters?: import('../types/ticketing').AdminOrderListFilters) =>
    ['ticketing', 'admin-orders', filters] as const,
  itemSales: (id: string) => ['ticketing', 'item-sales', id] as const,
  checkInSummary: (id: string) => ['ticketing', 'check-in-summary', id] as const,
  orderDetail: (id: string) => ['ticketing', 'order-detail', id] as const,
  memberAllowance: ['ticketing', 'member-allowance'] as const,
  /**
   * Per-ticket admin manifest. Keyed by item id + a normalized
   * representation of the status filter. The filter is sorted before
   * being joined so `['valid','used']` and `['used','valid']` share a
   * cache entry — they produce identical backend results, no reason
   * to double-fetch.
   */
  manifest: (id: string, statusFilter?: readonly string[]) =>
    [
      'ticketing',
      'manifest',
      id,
      statusFilter && statusFilter.length > 0
        ? [...statusFilter].sort().join(',')
        : 'all',
    ] as const,
};

export const DONATION_KEYS = {
  all: ['donations'] as const,
  campaigns: ['donations', 'campaigns'] as const,
  campaign: (id: string) => ['donations', 'campaign', id] as const,
  adminStats: ['donations', 'admin-stats'] as const,
};

export const EVENT_KEYS = {
  all: ['events'] as const,
  list: ['events', 'list'] as const,
  detail: (id: string) => ['events', 'detail', id] as const,
  upcoming: (params?: Record<string, unknown>) => ['events', 'upcoming', params] as const,
};

export const RSVP_KEYS = {
  all: ['rsvp'] as const,
  myRsvp: (eventId: string) => ['rsvp', 'my', eventId] as const,
  eventRsvps: (eventId: string) => ['rsvp', 'event', eventId] as const,
  stats: (eventId: string) => ['rsvp', 'stats', eventId] as const,
};

export const SURVEY_KEYS = {
  all: ['survey'] as const,
  myResponse: (eventId: string) => ['survey', 'my', eventId] as const,
  eventResponses: (eventId: string) => ['survey', 'event', eventId] as const,
};

export const DOCUMENT_KEYS = {
  all: ['documents'] as const,
  list: (filters?: DocumentFilters) => [...DOCUMENT_KEYS.all, 'list', filters] as const,
  detail: (id: string) => [...DOCUMENT_KEYS.all, 'detail', id] as const,
  downloadUrl: (id: string) => [...DOCUMENT_KEYS.all, 'download', id] as const,
  folders: () => [...DOCUMENT_KEYS.all, 'folders'] as const,
  folder: (id: string) => [...DOCUMENT_KEYS.all, 'folder', id] as const,
};

export const GALLERY_KEYS = {
  all: ['gallery'] as const,
  albums: () => [...GALLERY_KEYS.all, 'albums'] as const,
  album: (id: string) => [...GALLERY_KEYS.all, 'album', id] as const,
  albumMedia: (albumId: string, params?: Record<string, unknown>) =>
    [...GALLERY_KEYS.all, 'albumMedia', albumId, params] as const,
  albumMediaRoot: (albumId: string) => [...GALLERY_KEYS.all, 'albumMedia', albumId] as const,
  media: (id: string) => [...GALLERY_KEYS.all, 'media', id] as const,
  comments: (mediaId: string) => [...GALLERY_KEYS.all, 'comments', mediaId] as const,
  likes: (mediaId: string) => [...GALLERY_KEYS.all, 'likes', mediaId] as const,
  exportJob: (jobId: string) => [...GALLERY_KEYS.all, 'export', jobId] as const,
};
